/**
 * NotificationPanel Component
 * Displays a dropdown panel with notifications list
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Fade
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  DoneAll as DoneAllIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationItem from './NotificationItem';

const NotificationPanel = ({ onClose }) => {
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    fetchNotifications
  } = useNotifications();

  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Call fetchNotifications to get full notification list
      await fetchNotifications();
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  const handleNotificationClick = (notification) => {
    // Handle notification click - navigate to relevant page
    try {
      // Parse data if it's a string
      let parsedData = notification.data;
      if (typeof notification.data === 'string') {
        parsedData = JSON.parse(notification.data);
      }
      
      // Check for ticketUrl first, then fallback to ticketId
      if (parsedData?.ticketUrl) {
        window.location.href = parsedData.ticketUrl;
      } else if (parsedData?.ticketId) {
        // Fallback to constructing URL from ticketId
        window.location.href = `/admin/tickets/${parsedData.ticketId}`;
      }
    } catch (error) {
      console.error('Error parsing notification data:', error);
    }
    
    if (onClose) {
      onClose();
    }
  };

  // Filter notifications based on active tab
  const filteredNotifications = activeTab === 0 
    ? notifications 
    : notifications.filter(n => !n.read);

  return (
    <Fade in={true}>
      <Paper
        elevation={8}
        sx={{
          width: 420,
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            pb: 1,
            background: 'linear-gradient(135deg, #064d51 0%, #0a7075 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Notifications
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
              </IconButton>
              
              {unreadCount > 0 && (
                <IconButton
                  size="small"
                  onClick={handleMarkAllAsRead}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                  }}
                >
                  <DoneAllIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                color: 'rgba(255, 255, 255, 0.7)',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&.Mui-selected': {
                  color: 'white'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'white'
              }
            }}
          >
            <Tab label={`All (${notifications.length})`} />
            <Tab label={`Unread (${unreadCount})`} />
          </Tabs>
        </Box>

        <Divider />

        {/* Notifications List */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: 450,
            '&::-webkit-scrollbar': {
              width: 8
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }
            }
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 8
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                px: 3,
                textAlign: 'center'
              }}
            >
              <NotificationsIcon
                sx={{
                  fontSize: 64,
                  color: 'text.disabled',
                  mb: 2,
                  opacity: 0.3
                }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No notifications
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {activeTab === 0 
                  ? "You're all caught up!"
                  : "No unread notifications"}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onClick={handleNotificationClick}
                />
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        {filteredNotifications.length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Button
                fullWidth
                size="small"
                sx={{
                  textTransform: 'none',
                  color: 'primary.main',
                  fontWeight: 500
                }}
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Fade>
  );
};

export default NotificationPanel;
